import type { PresenceChannelMember } from 'c-auth-module/Types';
import type { CommonThunk } from 'c-types';
import type { Skin } from 'c-entity-types';
import { getDefaultSkinForUser, storeDefaultSkin, storeDefaultSkinForUser } from 'c-lib';
import { DEFAULT_SKIN_ID } from 'c-wrapper/Slices/UI/ui-selectors';
import * as authSelectors from 'c-auth-module/Slices/Auth/auth-selectors';
import { uiActions } from './ui-slice';

export const toggleDebug = (): CommonThunk => async dispatch => {
    dispatch(uiActions.toggleDebug());
};

/**
 * set the skin which is being previewed within the skin editing page
 */
export const setPlayAroundSkin =
    (skin: Skin | null): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.setPlayAroundTheme(skin));
    };

/**
 * Set the user's skin after logging
 *
 * We only want to set it if no skin is currently set as an override.
 *
 * For example, users can have multiple skins available to them.
 *
 * They they've chosen skin #2, we don't want to reselect skin #1 when they log in.
 *
 * @param skins
 */
export const setPreviewThemeOnLogin =
    ({ skins, userId }: { skins?: Skin[]; userId?: number }): CommonThunk =>
    async dispatch => {
        const availableSkins = Array.isArray(skins) ? skins : [];
        const storedSkin = getDefaultSkinForUser(userId);
        const storedDefaultSkin =
            storedSkin != null && storedSkin.id === DEFAULT_SKIN_ID ? storedSkin : undefined;
        const storedSkinFromList =
            storedSkin && availableSkins.length > 0
                ? availableSkins.find(availableSkin => availableSkin?.id === storedSkin.id)
                : undefined;
        const nextSkin =
            storedDefaultSkin ??
            storedSkinFromList ??
            (availableSkins.length > 0 ? availableSkins[0] : storedSkin);

        if (nextSkin != null) {
            dispatch(setPreviewTheme(nextSkin, true, userId));
            return;
        }

        dispatch(setPreviewTheme(null));
    };

/**
 * Choose a new theme to display on the user's GUI
 * @param skin
 * @param storeSkin - pass as true if you want this to be stored in localstorage
 */
export const setPreviewTheme =
    (skin: Skin | null | undefined, storeSkin = false, userId?: number): CommonThunk =>
    async (dispatch, getState) => {
        dispatch(uiActions.setPreviewTheme(skin));

        if (storeSkin && skin != null) {
            const resolvedUserId = userId ?? authSelectors.authenticatedUser(getState())?.id;
            storeDefaultSkin(skin);
            storeDefaultSkinForUser(resolvedUserId, skin);
        }
    };

export const toggleDrawerOpen =
    (open: boolean): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.toggleDrawerOpen(open));
    };

export const toggleNotesDrawerOpen =
    (open: boolean): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.toggleNotesDrawerOpen(open));
    };

export const setCrmMembers =
    (members: PresenceChannelMember[]): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.setCrmMembers(members));
    };

export const addCrmMember =
    (member: PresenceChannelMember): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.addCrmMember(member));
    };

export const removeCrmMember =
    (member: PresenceChannelMember): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.removeCrmMember(member));
    };

export const setPresenceMembers =
    (members: PresenceChannelMember[]): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.setPageMembers(members));
    };

export const addPresenceMember =
    (member: PresenceChannelMember): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.addPageMember(member));
    };

export const removePresenceMember =
    (member: PresenceChannelMember): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.removePageMember(member));
    };

export const updateLayoutMetaData =
    (title: string): CommonThunk =>
    async dispatch => {
        dispatch(uiActions.setLayoutMetaData({ title }));
    };
